/**
 * IdP Connections list page - cards for each connection
 */

import { useNavigate } from 'react-router-dom'
import { Link2, Plus, RefreshCw } from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useIdPConnections } from '@/hooks/use-idp-connections'
import { ConnectionStatusBadge } from '@/components/identity/sync-status-badge'

const providerIcon: Record<string, string> = {
  LDAP: 'LDAP',
  AZURE_AD: 'Azure AD',
  OKTA: 'Okta',
  GOOGLE_WORKSPACE: 'Google',
  CUSTOM_REST_API: 'REST API',
}

export function ConnectionsPage() {
  const navigate = useNavigate()
  const { data: connections, isLoading, refetch } = useIdPConnections()

  const connectionList = connections ?? []

  return (
    <>
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Link2 className="h-4 w-4" />
          <h1 className="text-base font-medium">IdP Connections</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => navigate('/identity/connections/new')}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            Add Connection
          </Button>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 p-4 md:p-6">
        <p className="text-sm text-muted-foreground">
          Configure and manage identity provider connections for user attribute synchronization
        </p>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-40" />
            ))}
          </div>
        ) : connectionList.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
            <Link2 className="h-12 w-12 text-muted-foreground/30" />
            <div className="text-center">
              <p className="text-sm font-medium">No connections configured</p>
              <p className="text-sm text-muted-foreground">
                Connect an Identity Provider to start syncing user attributes
              </p>
            </div>
            <Button onClick={() => navigate('/identity/connections/new')}>
              <Plus className="mr-2 h-4 w-4" />
              Add Connection
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {connectionList.map((conn) => (
              <Card
                key={conn.id}
                className="cursor-pointer transition-[box-shadow,border-color] hover:shadow-md hover:border-primary/30"
                onClick={() => navigate(`/identity/connections/${conn.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{conn.name}</CardTitle>
                      <CardDescription>
                        {providerIcon[conn.providerType] ?? conn.providerType}
                      </CardDescription>
                    </div>
                    <ConnectionStatusBadge status={conn.status} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    {conn.description && <p>{conn.description}</p>}
                    <div className="flex items-center justify-between">
                      <span>Sync: {conn.syncEnabled ? 'Enabled' : 'Disabled'}</span>
                      {conn.syncSchedule && <span>{conn.syncSchedule}</span>}
                    </div>
                    {conn.lastTestAt && (
                      <p>
                        Last tested: {new Date(conn.lastTestAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
